import { useId, useState } from 'react';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong, maLoi } from '../../../lib/errors';
import { ChonPhongBan } from '../../co-cau-to-chuc/components/ChonPhongBan';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { KHOA_LOI_TEN } from '../constants';
import { DAI_TOI_DA, kiemTen } from '../nghiep-vu/thao-tac';
import type { UserProfile, UserUpdate } from '../types';

/**
 * Hộp thoại sửa thông tin người dùng (`PATCH /api/v1/users/{id}`).
 *
 * Sửa tên hiển thị và phòng ban (`department_id`, ADR-0014; `null` là bỏ khỏi
 * phòng ban). Chỉ gửi trường đã đổi. Email và vai trò lớp 1 không sửa ở đây:
 * email là tên đăng nhập, vai trò lớp 1 chỉ đặt lúc mời (ADR-0004 mục 4).
 */
export function FormSuaNguoiDung({
  nguoi,
  open,
  dangLuu,
  onClose,
  onLuu,
}: {
  nguoi: UserProfile;
  open: boolean;
  dangLuu: boolean;
  onClose: () => void;
  /** Chỉ chứa trường đã đổi. */
  onLuu: (du: Pick<UserUpdate, 'display_name' | 'department_id'>) => Promise<unknown>;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();

  const [ten, setTen] = useState(nguoi.display_name);
  const [phongBan, setPhongBan] = useState<string | null>(nguoi.department_id);
  const [daGui, setDaGui] = useState(false);
  const [loiServer, setLoiServer] = useState<{ loi: unknown; truong: Record<string, string> } | null>(
    null,
  );

  const maTen = kiemTen(ten);
  const loiTen =
    (maTen && (daGui || maTen === 'dai')
      ? t(KHOA_LOI_TEN[maTen], { so: DAI_TOI_DA.display_name })
      : undefined) ?? loiServer?.truong['/display_name'];
  const doiTen = ten.trim() !== nguoi.display_name;
  const doiPhongBan = phongBan !== nguoi.department_id;
  const coThayDoi = doiTen || doiPhongBan;
  // Phòng ban vừa bị xoá ở phiên khác: lỗi nói về đúng ô phòng ban.
  const loiPhongBan =
    loiServer && maLoi(loiServer.loi) === 'DEPARTMENT_NOT_FOUND' ? dichLoi(loiServer.loi) : undefined;
  // Lỗi đã nằm dưới một ô thì không lặp lại ở dải chung.
  const loiChung =
    loiServer && !loiServer.truong['/display_name'] && !loiPhongBan ? loiServer.loi : null;

  const gui = async () => {
    setDaGui(true);
    if (maTen || !coThayDoi || dangLuu) return;
    setLoiServer(null);
    try {
      await onLuu({
        ...(doiTen ? { display_name: ten.trim() } : {}),
        ...(doiPhongBan ? { department_id: phongBan } : {}),
      });
    } catch (err) {
      setLoiServer({ loi: err, truong: loiTheoTruong(err) });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('nguoiDung.suaTieuDe', { ten: nguoi.display_name })}
      width={480}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loiChung) && <LoiTrongHop>{dichLoi(loiChung)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button
              variant="primary"
              size="md"
              htmlType="submit"
              form={formId}
              loading={dangLuu}
              disabled={!coThayDoi}
            >
              {dangLuu ? t('chung.dangLuu') : t('nguoiDung.luuThongTin')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          void gui();
        }}
      >
        <Field label={t('nguoiDung.fTenHienThi')} required error={loiTen}>
          {(p) => (
            <Input
              {...p}
              autoFocus
              value={ten}
              onChange={(e) => {
                setTen(e.target.value);
                setLoiServer(null);
              }}
            />
          )}
        </Field>
        <ChonPhongBan
          value={phongBan}
          error={loiPhongBan}
          onChange={(id) => {
            setPhongBan(id);
            setLoiServer(null);
          }}
        />
        {nguoi.email && (
          <div className="space-y-1.5">
            <span className="block text-sm font-medium text-content-secondary">
              {t('nguoiDung.fEmail')}
            </span>
            <span className="block break-all text-sm text-content-primary">{nguoi.email}</span>
            <p className="text-xs text-content-muted">{t('nguoiDung.fEmailKhongSua')}</p>
          </div>
        )}
      </form>
    </Dialog>
  );
}
